import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Notifications as NotificationsIcon } from "@mui/icons-material";
import notificationsApi from "../../api/notifications";

interface NotificationDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

const NotificationDialog: React.FC<NotificationDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !body.trim()) {
      setError("Title and body are required");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await notificationsApi.sendIndividualNotification(
        userId,
        title.trim(),
        body.trim()
      );

      setSuccess(`Notification sent successfully to ${userName} (${response.devicesCount} device${response.devicesCount > 1 ? 's' : ''})`);
      
      // Reset form
      setTitle("");
      setBody("");
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(null);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTitle("");
      setBody("");
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <NotificationsIcon color="primary" />
        Send Notification to {userName}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Send a push notification to this user's registered devices.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            label="Notification Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            margin="normal"
            placeholder="Enter notification title"
            disabled={loading}
          />

          <TextField
            label="Notification Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            required
            margin="normal"
            multiline
            rows={4}
            placeholder="Enter notification message"
            disabled={loading}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <NotificationsIcon />}
            disabled={loading || !title.trim() || !body.trim()}
          >
            {loading ? "Sending..." : "Send Notification"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NotificationDialog; 